/**
 * Auto-add availability filter to search and collection page URLs
 * This ensures that the availability filter is always set to "In stock" (value "1")
 * and is hidden from the UI on search and collection pages.
 */

const HAS_IMAGE_FILTER_PARAM = 'filter.p.tag';
const HAS_IMAGE_FILTER_VALUE = 'has-image';
const ADOPTED_TITLE_FILTER_VALUE = 'adoptedtitle';
const ADOPTED_SUPPLY_FILTER_VALUE = 'adoptedsupply';
const CLEAR_ALL_BOUND_ATTR = 'data-availability-clear-bound';

function ensureHasImageFilter(searchParams) {
  const hasHasImage = searchParams
    .getAll(HAS_IMAGE_FILTER_PARAM)
    .some((value) => value === HAS_IMAGE_FILTER_VALUE);

  if (!hasHasImage) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, HAS_IMAGE_FILTER_VALUE);
    return true;
  }

  return false;
}

function ensureAdoptedTitleFilter(searchParams) {
  const hasAdoptedTitle = searchParams
    .getAll(HAS_IMAGE_FILTER_PARAM)
    .some((value) => value === ADOPTED_TITLE_FILTER_VALUE);

  if (!hasAdoptedTitle) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, ADOPTED_TITLE_FILTER_VALUE);
    return true;
  }

  return false;
}

function ensureAdoptedSupplyFilter(searchParams) {
  const hasAdoptedSupply = searchParams
    .getAll(HAS_IMAGE_FILTER_PARAM)
    .some((value) => value === ADOPTED_SUPPLY_FILTER_VALUE);

  if (!hasAdoptedSupply) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, ADOPTED_SUPPLY_FILTER_VALUE);
    return true;
  }

  return false;
}

/**
 * Collapse repeated identical key/value pairs (first occurrence wins, order kept).
 * Shopify ignores filter.v.availability when it appears twice, so the unfiltered
 * result set gets paginated before out-of-stock products are hidden.
 * @param {URLSearchParams} searchParams
 * @returns {boolean} true when at least one duplicate pair was removed
 */
function dedupeSearchParams(searchParams) {
  const entries = [...searchParams];
  const seen = new Set();
  const unique = [];

  for (const [key, value] of entries) {
    const pair = JSON.stringify([key, value]);
    if (seen.has(pair)) continue;
    seen.add(pair);
    unique.push([key, value]);
  }

  if (unique.length === entries.length) return false;

  for (const key of new Set(unique.map(([name]) => name))) {
    searchParams.delete(key);
  }
  for (const [key, value] of unique) {
    searchParams.append(key, value);
  }

  return true;
}

function ensureAvailabilityFilter() {
  const url = new URL(window.location.href);
  const pathname = url.pathname;

  const isSearchPage = pathname === '/search' || pathname.startsWith('/search/');
  const isCollectionPage = pathname.startsWith('/collections/');

  if (!isSearchPage && !isCollectionPage) return;

  const duplicatesRemoved = dedupeSearchParams(url.searchParams);
  const hasAvailabilityFilter = url.searchParams.has('filter.v.availability');

  if (!hasAvailabilityFilter) {
    url.searchParams.set('filter.v.availability', '1');
  }

  const hasImageAdded = ensureHasImageFilter(url.searchParams);
  const adoptedTitleAdded = ensureAdoptedTitleFilter(url.searchParams);
  const adoptedSupplyAdded = ensureAdoptedSupplyFilter(url.searchParams);

  if (duplicatesRemoved || !hasAvailabilityFilter || hasImageAdded || adoptedTitleAdded || adoptedSupplyAdded) {
    // Shopify must receive these filters before it paginates. Replacing only
    // browser history leaves the unfiltered HTML behind (sparse collection pages).
    // One canonical navigation avoids both an extra Back entry and the old
    // filter-event/observer loop. Already-canonical requests do not navigate.
    window.location.replace(url.toString());
    return true;
  }
}

/**
 * @param {Element} component
 */
function hideFacetRemovePill(component) {
  if (!(component instanceof HTMLElement)) return;

  component.style.display = 'none';
  component.setAttribute('hidden', 'true');
  component.style.visibility = 'hidden';
  component.style.opacity = '0';
  component.style.height = '0';
  component.style.margin = '0';
  component.style.padding = '0';
  component.style.overflow = 'hidden';
  component.style.pointerEvents = 'none';
}

/**
 * Hide always-on filter pills (In stock / has-image / adoptedtitle / adoptedsupply) from the UI.
 * Style-only — must not mutate the DOM tree (no replaceChild) or body observers will loop.
 */
function hideRequiredFilterPills() {
  document.querySelectorAll('facet-remove-component').forEach((component) => {
    const textContent = (component.textContent || '').trim().toLowerCase();
    const dataUrl = component.getAttribute('data-url') || '';

    const isInStock = textContent === 'in stock';
    const isHasImage =
      textContent === 'has-image' ||
      textContent === 'has image' ||
      textContent.includes('has-image') ||
      textContent.includes('has image') ||
      dataUrl.includes('filter.p.tag=has-image') ||
      dataUrl.includes('filter.p.tag%3Dhas-image');
    const isAdoptedTitle =
      textContent === 'adoptedtitle' ||
      textContent.includes('adoptedtitle') ||
      dataUrl.includes('filter.p.tag=adoptedtitle') ||
      dataUrl.includes('filter.p.tag%3Dadoptedtitle');
    const isAdoptedSupply =
      textContent === 'adoptedsupply' ||
      textContent.includes('adoptedsupply') ||
      dataUrl.includes('filter.p.tag=adoptedsupply') ||
      dataUrl.includes('filter.p.tag%3Dadoptedsupply');

    if (isInStock || isHasImage || isAdoptedTitle || isAdoptedSupply) {
      hideFacetRemovePill(component);
    }
  });
}

function hideAvailabilityFilterUi() {
  document
    .querySelectorAll('[data-filter-param-name="v-availability"], [name*="v.availability"]')
    .forEach((filter) => {
      const facetItem = filter.closest('.facets__item');
      if (facetItem instanceof HTMLElement) facetItem.style.display = 'none';
    });

  document
    .querySelectorAll(
      'input[name="filter.p.tag"][value="has-image"], input[name="filter.p.tag"][value="adoptedtitle"], input[name="filter.p.tag"][value="adoptedsupply"]'
    )
    .forEach((filter) => {
      const facetItem = filter.closest('.facets__item');
      if (facetItem instanceof HTMLElement) facetItem.style.display = 'none';
    });

  document.querySelectorAll('.facets__label').forEach((label) => {
    const text = (label.textContent || '').toLowerCase();
    if (
      text.includes('availability') ||
      text.includes('has image') ||
      text.includes('adoptedtitle') ||
      text.includes('adoptedsupply')
    ) {
      const facetItem = label.closest('.facets__item');
      if (facetItem instanceof HTMLElement) facetItem.style.display = 'none';
    }
  });

  hideRequiredFilterPills();
}

/**
 * @param {MouseEvent} event
 */
function handleClearAllClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const clearButton = target.closest('.facets__clear-all-link');
  if (!(clearButton instanceof HTMLElement)) return;

  const component = clearButton.closest('facet-remove-component');
  if (!component) return;

  event.preventDefault();
  event.stopPropagation();

  const dataUrl = component.getAttribute('data-url') || '';
  const currentUrl = new URL(window.location.href);
  const isSearchPage =
    currentUrl.pathname === '/search' || currentUrl.pathname.startsWith('/search/');

  let targetUrl;
  try {
    targetUrl = new URL(dataUrl, window.location.origin);
  } catch {
    if (dataUrl.startsWith('?')) {
      targetUrl = new URL(window.location.pathname + dataUrl, window.location.origin);
    } else {
      targetUrl = new URL(dataUrl, window.location.origin);
    }
  }

  const basePath = isSearchPage ? '/search' : window.location.pathname;
  const newUrl = new URL(basePath, window.location.origin);

  if (isSearchPage) {
    const searchQuery =
      currentUrl.searchParams.get('q') || targetUrl.searchParams.get('q') || '';
    if (searchQuery) {
      newUrl.searchParams.set('q', searchQuery);
    }

    const optionsPrefix =
      currentUrl.searchParams.get('options[prefix]') ||
      targetUrl.searchParams.get('options[prefix]') ||
      'last';
    newUrl.searchParams.set('options[prefix]', optionsPrefix);
  }

  const sortBy =
    currentUrl.searchParams.get('sort_by') ||
    targetUrl.searchParams.get('sort_by') ||
    (isSearchPage ? 'relevance' : 'best-selling');
  if (sortBy) {
    newUrl.searchParams.set('sort_by', sortBy);
  }

  newUrl.searchParams.set('filter.v.availability', '1');
  ensureHasImageFilter(newUrl.searchParams);
  ensureAdoptedTitleFilter(newUrl.searchParams);
  ensureAdoptedSupplyFilter(newUrl.searchParams);

  const facetsForm = component.closest('facets-form-component');
  if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
    facetsForm.updateFiltersByURL(newUrl.toString());
  } else {
    window.location.href = newUrl.toString();
  }
}

function bindClearAllDelegation() {
  if (document.documentElement.hasAttribute(CLEAR_ALL_BOUND_ATTR)) return;
  document.documentElement.setAttribute(CLEAR_ALL_BOUND_ATTR, 'true');
  document.addEventListener('click', handleClearAllClick, true);
}

function observeFacetPills() {
  let scheduled = false;

  const scheduleHide = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      hideRequiredFilterPills();
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          node.matches?.('facet-remove-component') ||
          node.querySelector?.('facet-remove-component')
        ) {
          scheduleHide();
          return;
        }
      }
    }
  });

  const roots = document.querySelectorAll('.facets-remove, facets-form-component, results-list');
  if (roots.length > 0) {
    roots.forEach((root) => {
      observer.observe(root, { childList: true, subtree: true });
    });
  } else if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

function initializeAvailabilityFilter() {
  if (ensureAvailabilityFilter()) return;
  hideAvailabilityFilterUi();
  bindClearAllDelegation();
  observeFacetPills();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAvailabilityFilter, { once: true });
} else {
  initializeAvailabilityFilter();
}
